/**
 * The single SPA-facing entry point for the CORTEX HTTP API.
 *
 * Importers should _only_ touch `cortexApi` (and the type re-exports
 * below), never `createApiClient` directly. That keeps the swap surface
 * tiny when the Cloud Run cutover changes the base URL convention.
 *
 * The base URL comes from `VITE_API_BASE_URL`. When unset, the SPA
 * falls back to the legacy in-browser shim and the `AuthContext`
 * reports `unconfigured`.
 *
 * **Dev-bypass subject (PR #4d)**
 *
 *   When the SPA runs locally without a Cloudflare Access proxy in
 *   front of the API, requests carry no CF JWT. The API's
 *   `requireAuth` middleware accepts an `X-Dev-Auth-Subject` header in
 *   `MODE=development` only (see `apps/api/src/middleware/requireAuth.ts`).
 *
 *   The CloudflareLoginScreen lets the developer pick a CF Access
 *   subject and stores it in `localStorage`. `cortexApi`'s default-
 *   header callback reads the current value on every request, so a
 *   subject change takes effect on the next API call without re-
 *   creating the client.
 *
 *   In production this header is harmless — the API rejects it unless
 *   `MODE=development`. The SPA still injects it (the cost is one HTTP
 *   header) for code-path simplicity.
 */

import {
  createApiClient,
  isApiClientError,
  type ApiClient,
  type ApiClientError,
  type ClientErrorCode,
} from '@cortex/domain/client';

export type { ApiClientError, ClientErrorCode };
export { isApiClientError };
export type { HealthResponse } from '@cortex/domain';

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').trim();

export const isApiConfigured = (): boolean => baseUrl.length > 0;

const DEV_SUBJECT_STORAGE_KEY = 'cortex:devAuthSubject';

/**
 * Read the developer's currently-selected CF Access subject. Returns
 * `null` when no subject has been picked (production usage, or before
 * the dev picker has been touched).
 */
export const getDevAuthSubject = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(DEV_SUBJECT_STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

/**
 * Persist the developer's CF Access subject. Pass `null` to clear it
 * (used on logout from dev-mode).
 */
export const setDevAuthSubject = (subject: string | null): void => {
  if (typeof window === 'undefined') return;
  try {
    if (subject === null || subject.length === 0) {
      window.localStorage.removeItem(DEV_SUBJECT_STORAGE_KEY);
    } else {
      window.localStorage.setItem(DEV_SUBJECT_STORAGE_KEY, subject);
    }
  } catch {
    /* localStorage unavailable (private mode etc.) — silently no-op */
  }
};

export const cortexApi: ApiClient = createApiClient({
  baseUrl: baseUrl || 'http://unset.local',
  defaultHeaders: () => {
    const headers: Record<string, string> = { 'X-CORTEX-Client': 'cortex-web' };
    const devSubject = getDevAuthSubject();
    if (devSubject !== null) {
      headers['X-Dev-Auth-Subject'] = devSubject;
    }
    return headers;
  },
});

/**
 * Helper for components: returns `{ ok: true, data }` on success and
 * `{ ok: false, error }` for any `ApiClientError`. Non-API errors
 * propagate (they indicate a programmer mistake, not a runtime
 * failure).
 */
export interface ApiSuccess<T> {
  readonly ok: true;
  readonly data: T;
}
export interface ApiFailure {
  readonly ok: false;
  readonly error: ApiClientError;
}
export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

export const tryApi = async <T>(promise: Promise<T>): Promise<ApiResult<T>> => {
  try {
    const data = await promise;
    return { ok: true, data };
  } catch (err) {
    if (isApiClientError(err)) {
      return { ok: false, error: err };
    }
    throw err;
  }
};
